// Recovery for lazy-chunk load failures.
//
// Every route-level view is `React.lazy`. After a redeploy the hashed chunk a
// running tab still references no longer exists; the SPA rewrite returns
// index.html, and the browser rejects the import ("'text/html' is not a valid
// JavaScript MIME type" in Safari, "Failed to fetch dynamically imported
// module" in Chromium). React caches the rejected promise, so re-rendering can
// never recover — only a full reload picks up the new asset manifest.

const RELOAD_GUARD_KEY = "navig8_chunk_reload_at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /is not a valid JavaScript MIME type/i,
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Unable to preload CSS/i,
  /error loading dynamically imported module/i,
];

// Set once this document has asked the browser to reload. A reload is not
// instantaneous: React keeps rendering until the navigation commits, and a
// lazy route whose import already failed still throws into the boundary in
// the meantime. The boundary reads this flag to show the calm "Updating…"
// status instead of a second error card or a duplicate reload.
let reloadInFlight = false;

// Background warm-up imports (src/utils/routeChunkWarmup.js) go through the
// same Vite preload helper, so a warm-up that trips over a fresh deploy would
// otherwise reload the page underneath the user. While one is pending the
// preload listener stands down; a user-triggered import that fails in the
// same window still reaches ViewErrorBoundary, which reloads on its own.
let backgroundImports = 0;

export function trackBackgroundImport(promise) {
  backgroundImports += 1;
  const release = () => { backgroundImports = Math.max(0, backgroundImports - 1); };
  return Promise.resolve(promise).then(
    (value) => { release(); return value; },
    (error) => { release(); throw error; },
  );
}

export function hasBackgroundImportPending() {
  return backgroundImports > 0;
}

export function isChunkLoadError(error) {
  if (!error) return false;
  const message = typeof error === "string"
    ? error
    : [error.name, error.message, error.reason?.message, error.payload?.message].filter(Boolean).join(" ");
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/** True after this document has already triggered a chunk-recovery reload. */
export function isReloadInFlight() {
  return reloadInFlight;
}

function readGuard(storage) {
  try {
    const raw = storage?.getItem(RELOAD_GUARD_KEY);
    const at = raw ? Number(raw) : 0;
    return Number.isFinite(at) ? at : 0;
  } catch {
    return 0;
  }
}

/**
 * Reload the page once to pick up fresh assets. Returns true when a reload was
 * triggered (or is already in flight in this document), false when a reload
 * already happened within the guard window in a previous document and the
 * chunk still failed — the caller should then offer a manual refresh instead
 * of looping.
 */
export function reloadOnceForChunkError({ storage, location, now = Date.now() } = {}) {
  if (reloadInFlight) return true;
  const store = storage ?? (typeof window !== "undefined" ? window.sessionStorage : null);
  const loc = location ?? (typeof window !== "undefined" ? window.location : null);
  if (!loc || typeof loc.reload !== "function") return false;
  const lastReload = readGuard(store);
  if (lastReload && now - lastReload < RELOAD_GUARD_WINDOW_MS) return false;
  try {
    store?.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Private mode / disabled storage: still reload, we just lose the guard.
  }
  reloadInFlight = true;
  loc.reload();
  return true;
}

/**
 * Wire Vite's `vite:preloadError` event (fired when a dynamic import or its CSS
 * preload fails) to a one-shot reload. Returns an unsubscribe function.
 *
 * The event is deliberately NOT default-prevented. Vite only rethrows the
 * underlying import error when the event is not prevented; swallowing it made
 * the failed `import()` resolve to `undefined`, so `React.lazy` threw a
 * generic "reading 'default'" TypeError that the boundary could not recognise
 * as a chunk failure and rendered as a crash ("Something went wrong / Try
 * Again") while the reload was already landing.
 */
export function installChunkLoadRecovery(target = typeof window !== "undefined" ? window : null) {
  if (!target || typeof target.addEventListener !== "function") return () => {};
  const handler = () => {
    if (hasBackgroundImportPending()) return;
    reloadOnceForChunkError();
  };
  target.addEventListener("vite:preloadError", handler);
  return () => target.removeEventListener("vite:preloadError", handler);
}

export const __TEST_ONLY__ = {
  RELOAD_GUARD_KEY,
  RELOAD_GUARD_WINDOW_MS,
  resetReloadInFlight() { reloadInFlight = false; backgroundImports = 0; },
};
